import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  MapPin,
  Mail,
  Phone,
  Globe,
  Github,
  Linkedin,
  Download,
  Star,
  FileText,
  ExternalLink,
  Edit,
} from "lucide-react";
import Navbar from "../components/common/Navbar";
import Modal from "../components/common/Modal";
import FileViewer from "../components/common/FileViewer";
import FileList from "../components/common/FileList";
import CollapsibleSection from "../components/common/CollapsibleSection";
import { mockUserData, type UserProfile } from "../data/mockUserData";
import {
  DEFAULT_EXPANDED_SECTIONS,
  SKILL_CATEGORIES,
} from "../constants/profile";
import { formatDate, getSkillLevelColor } from "../utils/profileUtils";

/**
 * UserProfilePage Component
 * Displays a comprehensive user profile with collapsible sections
 * Features: Experience, Education, Skills, Portfolio, Certifications, etc.
 */
const UserProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();

  // State management for UI interactions
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(DEFAULT_EXPANDED_SECTIONS)
  );
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showPortfolioFileModal, setShowPortfolioFileModal] = useState(false);

  // Mock user data - in production, this would come from an API
  const userData: UserProfile = mockUserData;

  // Event handlers
  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Cover Image */}
      <div className="relative h-64 bg-gradient-to-r from-blue-600 to-purple-600">
        <img
          src={userData.coverImage}
          alt="Cover"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
      </div>

      {/* Profile Header */}
      <div className="container mx-auto px-4 -mt-20 pb-8 relative z-10">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Profile Header */}
          <div className="px-8 py-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <img
                src={userData.avatar}
                alt={userData.name}
                className="w-32 h-32 rounded-full border-4 border-white shadow-lg"
              />
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {userData.name}
                </h1>
                <p className="text-xl text-gray-600 mb-4">{userData.title}</p>
                <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-4">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{userData.location}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    <a
                      href={`mailto:${userData.email}`}
                      className="hover:underline"
                    >
                      {userData.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    <a
                      href={`tel:${userData.phone}`}
                      className="hover:underline"
                    >
                      {userData.phone}
                    </a>
                  </div>
                </div>
                <div className="flex gap-3 mb-4">
                  <a
                    href={userData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    <Globe className="w-4 h-4" />
                    <span>웹사이트</span>
                  </a>
                  <a
                    href={userData.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    <Github className="w-4 h-4" />
                    <span>GitHub</span>
                  </a>
                  <a
                    href={userData.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </a>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowResumeModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>이력서 다운로드</span>
                  </button>
                  <button
                    onClick={() => setShowPortfolioFileModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    <span>포트폴리오 파일</span>
                  </button>
                  <button
                    onClick={() =>
                      (window.location.href = `/profile/${username}/edit`)
                    }
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    <span>프로필 수정</span>
                  </button>
                  <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                    연락하기
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="px-8 py-6 space-y-8">
            {/* About Section */}
            <CollapsibleSection
              title="자기소개"
              isExpanded={expandedSections.has("about")}
              onToggle={() => toggleSection("about")}
            >
              <p className="text-gray-700 leading-relaxed">{userData.about}</p>
            </CollapsibleSection>

            {/* Experience Section */}
            <CollapsibleSection
              title="경력"
              isExpanded={expandedSections.has("experience")}
              onToggle={() => toggleSection("experience")}
            >
              <div className="space-y-6">
                {userData.experience.map((exp) => (
                  <div key={exp.id} className="border-l-4 border-blue-500 pl-6">
                    <div className="flex items-start gap-4">
                      <img
                        src={exp.companyLogo}
                        alt={exp.company}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {exp.title}
                        </h3>
                        <p className="text-gray-600 font-medium">
                          {exp.company}
                        </p>
                        <p className="text-sm text-gray-500 mb-2">
                          {formatDate(exp.startDate)} -{" "}
                          {exp.endDate ? formatDate(exp.endDate) : "현재"}
                        </p>
                        <p className="text-gray-700 mb-3">{exp.description}</p>
                        <ul className="space-y-1">
                          {exp.achievements.map((achievement, index) => (
                            <li
                              key={index}
                              className="flex items-center gap-2 text-sm text-gray-600"
                            >
                              <Star className="w-3 h-3 text-yellow-500" />
                              <span>{achievement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Education Section */}
            <CollapsibleSection
              title="학력"
              isExpanded={expandedSections.has("education")}
              onToggle={() => toggleSection("education")}
            >
              <div className="space-y-4">
                {userData.education.map((edu) => (
                  <div
                    key={edu.id}
                    className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg"
                  >
                    <img
                      src={edu.schoolLogo}
                      alt={edu.school}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {edu.degree}
                      </h3>
                      <p className="text-gray-600 font-medium">
                        {edu.school} - {edu.field}
                      </p>
                      <p className="text-sm text-gray-500 mb-2">
                        {formatDate(edu.startDate)} - {formatDate(edu.endDate)}
                      </p>
                      <p className="text-sm text-gray-600">학점: {edu.gpa}</p>
                      <p className="text-sm text-gray-700 mt-2">
                        {edu.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Portfolio Section */}
            <CollapsibleSection
              title="프로젝트"
              isExpanded={expandedSections.has("portfolio")}
              onToggle={() => toggleSection("portfolio")}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {userData.portfolio.map((project) => (
                  <div
                    key={project.id}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {project.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-3">
                        {project.description}
                      </p>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {project.techStack.map((tech, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <a
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" />
                          <span>라이브 데모</span>
                        </a>
                        <a
                          href={project.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 transition-colors"
                        >
                          <Github className="w-3 h-3" />
                          <span>GitHub</span>
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Open Source Contributions Section */}
            <CollapsibleSection
              title="오픈소스 기여"
              isExpanded={expandedSections.has("opensource")}
              onToggle={() => toggleSection("opensource")}
            >
              <div className="space-y-4">
                {userData.openSourceContributions.map((contribution) => (
                  <div
                    key={contribution.id}
                    className="border border-gray-200 rounded-lg p-6"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {contribution.projectName}
                          </h3>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                            {contribution.role}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-2">
                          {contribution.description}
                        </p>
                        <p className="text-sm text-gray-500">
                          Repository: {contribution.repository}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-sm text-gray-500 mb-1">
                          <Star className="w-4 h-4 text-yellow-500" />
                          <span>{contribution.stars.toLocaleString()}</span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {contribution.contributions}개 기여
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="px-2 py-1 bg-gray-100 rounded-full">
                        {contribution.language}
                      </span>
                      <span>
                        마지막 기여: {formatDate(contribution.lastContribution)}
                      </span>
                      <a
                        href={`https://github.com/${contribution.repository}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <Github className="w-4 h-4" />
                        GitHub 보기
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Certifications Section */}
            <CollapsibleSection
              title="자격증"
              isExpanded={expandedSections.has("certifications")}
              onToggle={() => toggleSection("certifications")}
            >
              <div className="space-y-4">
                {userData.certifications.map((cert) => (
                  <div
                    key={cert.id}
                    className="border border-gray-200 rounded-lg p-6"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {cert.name}
                        </h3>
                        <p className="text-gray-600 font-medium">
                          {cert.issuer}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">
                          발급일: {formatDate(cert.issueDate)}
                        </p>
                        {cert.expiryDate && (
                          <p className="text-sm text-gray-500">
                            만료일: {formatDate(cert.expiryDate)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <span>자격증 ID: {cert.credentialId}</span>
                      <a
                        href={cert.verificationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        검증하기
                      </a>
                    </div>
                    {cert.expiryDate && (
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${Math.max(
                              0,
                              Math.min(
                                100,
                                ((new Date(cert.expiryDate).getTime() -
                                  new Date().getTime()) /
                                  (new Date(cert.expiryDate).getTime() -
                                    new Date(cert.issueDate).getTime())) *
                                  100
                              )
                            )}%`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Recommendations Section */}
            <CollapsibleSection
              title="추천서"
              isExpanded={expandedSections.has("recommendations")}
              onToggle={() => toggleSection("recommendations")}
            >
              <div className="space-y-4">
                {userData.recommendations.map((rec) => (
                  <div
                    key={rec.id}
                    className="border border-gray-200 rounded-lg p-6"
                  >
                    <div className="flex items-start gap-4">
                      <img
                        src={rec.recommenderAvatar}
                        alt={rec.recommenderName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900">
                            {rec.recommenderName}
                          </h3>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-600">
                            {rec.recommenderTitle}
                          </span>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-600">{rec.company}</span>
                        </div>
                        <p className="text-gray-700 mb-2">{rec.message}</p>
                        <p className="text-sm text-gray-500">
                          {formatDate(rec.receivedAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Skills Section */}
            <CollapsibleSection
              title="기술 스택"
              isExpanded={expandedSections.has("skills")}
              onToggle={() => toggleSection("skills")}
            >
              <div className="space-y-4">
                {SKILL_CATEGORIES.map((category) => (
                  <div key={category}>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {category}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {userData.skills
                        .filter((skill) => skill.category === category)
                        .map((skill, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full"
                          >
                            <span className="text-sm font-medium text-gray-700">
                              {skill.name}
                            </span>
                            <div
                              className={`w-2 h-2 rounded-full ${getSkillLevelColor(
                                skill.level
                              )}`}
                            ></div>
                            <span className="text-xs text-gray-500">
                              {skill.level}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          </div>
        </div>
      </div>

      {/* Resume Modal */}
      <Modal
        isOpen={showResumeModal}
        onClose={() => setShowResumeModal(false)}
        title="이력서 보기"
      >
        <FileViewer
          fileName={userData.resume.name}
          fileUrl={userData.resume.url}
          uploadDate={formatDate(userData.resume.uploadedAt)}
          fileSize={userData.resume.size}
        />
      </Modal>

      {/* Portfolio Files Modal */}
      <Modal
        isOpen={showPortfolioFileModal}
        onClose={() => setShowPortfolioFileModal(false)}
        title="포트폴리오 파일"
        maxWidth="max-w-4xl"
      >
        <FileList
          files={userData.portfolioFiles}
          emptyMessage="포트폴리오 파일이 없습니다."
        />
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            포트폴리오 파일을 다운로드하거나 새 탭에서 열어보세요
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default UserProfilePage;
